/**
 * RFC 9457 problem+json. One shape in, one renderer out (see components/ErrorView).
 *
 * The `type` URI is the stable contract (apps/api/src/lib/errors.ts); `title` and
 * `detail` are the human-facing strings and `category` is the moderation reason
 * the composer must surface instead of a generic failure (§7.3).
 */

export interface Problem {
  type: string;
  title: string;
  status: number;
  detail?: string;
  instance?: string;
  /** Present on `moderation-blocked`. */
  category?: string;
  /** Present on `forbidden`. */
  capability?: string;
  /** Present on `rate-limited`, in seconds. */
  retry_after?: number;
  /** Present on `adapter-unavailable`. */
  platform?: string;
  [key: string]: unknown;
}

const BASE = 'https://personamarketplace.dev/problems';

/** Short slug of a problem `type` URI, e.g. `moderation-blocked`. */
export function problemSlug(p: Problem): string {
  return p.type.startsWith(`${BASE}/`) ? p.type.slice(BASE.length + 1) : p.type;
}

export class ProblemError extends Error {
  readonly problem: Problem;

  constructor(problem: Problem) {
    super(problem.detail ?? problem.title);
    this.name = 'ProblemError';
    this.problem = problem;
  }

  get slug(): string {
    return problemSlug(this.problem);
  }

  get status(): number {
    return this.problem.status;
  }
}

/** A network or parse failure still has to render through the one error path. */
export function transportProblem(detail: string): Problem {
  return {
    type: `${BASE}/transport`,
    title: "Couldn't reach the server",
    status: 0,
    detail,
  };
}

export function isProblem(v: unknown): v is Problem {
  return (
    typeof v === 'object' &&
    v !== null &&
    typeof (v as Problem).title === 'string' &&
    typeof (v as Problem).status === 'number'
  );
}

export function toProblem(err: unknown): Problem {
  if (err instanceof ProblemError) return err.problem;
  if (isProblem(err)) return err;
  return transportProblem(err instanceof Error ? err.message : String(err));
}
